import { Component } from '@theme/component';
import { getRecipeAPI } from './recipe-api.js';

/**
 * Recipe Selector Component
 * Handles recipe selection for "Kapky na míru" products with support for multiple pieces
 *
 * @typedef {object} RecipeDraft
 * @property {string} localId - UUID generated on frontend
 * @property {number} [templateRecipeId] - ID of existing recipe (if selected from list)
 * @property {string} [name] - Recipe name
 * @property {'list' | 'text'} [creationMethod] - How the recipe was created
 * @property {number[]} [selectedProblems] - Selected problem IDs
 * @property {string[]} [customProblems] - Custom problem texts
 * @property {string} [healthIssues] - Health issues
 * @property {string} [childAge] - Child age
 * @property {string} [medications] - Medications
 * @property {string} [medicationsPurpose] - Medications purpose
 * @property {string} [desiredOutcome] - Desired outcome
 * @property {boolean} isEditable - Can be edited (true for new, false for loaded)
 * @property {string} displayName - Name to display in UI
 *
 * @typedef {object} RecipeSelectorRefs
 * @property {HTMLInputElement} emailInput - Email input field
 * @property {HTMLButtonElement} loadButton - Load recipes button
 * @property {HTMLElement} loadingIndicator - Loading spinner
 * @property {HTMLElement} errorMessage - Error message container
 * @property {HTMLElement} successMessage - Success message container
 * @property {HTMLElement} successText - Success message text
 * @property {HTMLElement} primaryAction - Primary action button container
 * @property {HTMLElement} divider - Divider between primary action and email search
 * @property {HTMLElement} emailSection - Email search section container
 * @property {HTMLElement} helperText - Helper text at the bottom
 * @property {HTMLInputElement} multipleRecipesCheckbox - Checkbox for multiple recipes mode
 * @property {HTMLElement} multipleRecipesToggle - Container for the checkbox (hidden when qty=1)
 * @property {HTMLElement} singleRecipeContainer - Container for single recipe dropdown
 * @property {HTMLElement} multipleRecipesContainer - Container for multiple recipes mode
 * @property {HTMLSelectElement} singleRecipeSelect - Dropdown for single recipe
 * @property {HTMLButtonElement} singleNewButton - Button to create new recipe
 * @property {HTMLElement} singleSelectedRecipe - Shows selected recipe (single mode)
 * @property {HTMLElement} singleSelectedName - Name of selected recipe
 * @property {HTMLButtonElement} singleEditButton - Edit button for draft recipe
 * @property {HTMLElement} slotsContainer - Container for multiple recipe slots
 *
 * @extends Component<RecipeSelectorRefs>
 */
class RecipeSelectorComponent extends Component {
  requiredRefs = ['emailInput'];

  /** @type {import('./recipe-api.js').RecipeAPI | null} */
  #api = null;

  /** @type {Array<{id: number, name: string}>} */
  #loadedRecipes = [];

  /** @type {RecipeDraft[]} */
  #createdDrafts = [];

  /** @type {'single' | 'multiple'} */
  #mode = 'single';

  /** @type {number} */
  #quantity = 1;

  /** @type {RecipeDraft | null} */
  #singleRecipe = null;

  /** @type {RecipeDraft[]} */
  #recipes = [];

  /** @type {boolean} */
  #isLoading = false;

  /** @type {string} */
  #customerEmail = '';

  connectedCallback() {
    super.connectedCallback();

    // Initialize API
    const apiUrl = this.dataset.apiUrl;
    const locale = this.dataset.locale || 'cs';

    if (apiUrl) {
      this.#api = getRecipeAPI(apiUrl, locale);
    }

    // Get customer email if logged in
    this.#customerEmail = this.dataset.customerEmail || '';
    if (this.refs.emailInput && this.#customerEmail) {
      this.refs.emailInput.value = this.#customerEmail;
    }

    // Initialize quantity from data attribute
    this.#quantity = parseInt(this.dataset.quantity || '1', 10);
    this.#updateMultipleToggleVisibility();

    // Initialize UI state (ensure selected recipe section is hidden initially)
    this.#updateSingleRecipeUI();

    // Get parent product form
    const productForm = this.closest('product-form-component');

    // Listen for quantity changes from quantity selector
    if (productForm) {
      productForm.addEventListener('quantity:changed', this.#onQuantityChanged);
      // Listen for recipe modal events on parent (modal is sibling, not child)
      productForm.addEventListener('recipe:created', this.#onRecipeCreated);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const productForm = this.closest('product-form-component');
    if (productForm) {
      productForm.removeEventListener('quantity:changed', this.#onQuantityChanged);
      productForm.removeEventListener('recipe:created', this.#onRecipeCreated);
    }
  }

  /**
   * Generate a unique local ID for drafts
   * @returns {string}
   */
  #generateLocalId() {
    return 'draft_' + Math.random().toString(36).substring(2, 15);
  }

  /**
   * Handle load button click
   */
  handleLoadRecipes() {
    this.#loadRecipes();
  }

  /**
   * Handle email input keydown (Enter to load)
   * @param {KeyboardEvent} event
   */
  handleEmailKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#loadRecipes();
    }
  }

  /**
   * Load recipes from API
   */
  async #loadRecipes() {
    if (this.#isLoading) return;

    const { emailInput } = this.refs;

    if (!this.#api || !emailInput) return;

    const email = emailInput.value.trim();
    if (!email || !email.includes('@')) {
      this.#showError(this.dataset.invalidEmailText || 'Zadejte platný e-mail');
      return;
    }

    // Skip if recipes for this email are already loaded
    if (email === this.#customerEmail && this.#loadedRecipes.length > 0) return;

    this.#isLoading = true;
    this.#showLoading(true);
    this.#hideError();
    this.#hideSuccess();

    try {
      const recipes = await this.#api.searchRecipes(email);
      this.#loadedRecipes = recipes;
      this.#customerEmail = email;
      this.#updateRecipeDropdowns();

      if (recipes.length === 0) {
        this.#showError(this.dataset.noRecipesText || 'Žádný uložený recept nebyl nalezen');
      } else {
        // Show success message with count
        const template = this.dataset.recipesLoadedText || 'Nalezeno receptů: {{ count }}';
        const message = template.replace('{{ count }}', recipes.length.toString());
        this.#showSuccess(message);
      }
    } catch (error) {
      console.warn('[MEG] Failed to load recipes:', error);
      this.#showError(this.dataset.loadErrorText || 'Chyba při načítání');
    } finally {
      this.#isLoading = false;
      this.#showLoading(false);
    }
  }

  /**
   * Show/hide loading indicator
   * @param {boolean} show
   */
  #showLoading(show) {
    const { loadingIndicator, loadButton } = this.refs;
    if (loadingIndicator) loadingIndicator.hidden = !show;
    if (loadButton) loadButton.disabled = show;
  }

  /**
   * Show error message
   * @param {string} message
   */
  #showError(message) {
    const { errorMessage } = this.refs;
    if (errorMessage) {
      errorMessage.textContent = message;
      errorMessage.hidden = false;
    }
  }

  /**
   * Hide error message
   */
  #hideError() {
    const { errorMessage } = this.refs;
    if (errorMessage) {
      errorMessage.hidden = true;
    }
  }

  /**
   * Show success message
   * @param {string} message
   */
  #showSuccess(message) {
    const { successMessage, successText } = this.refs;
    if (successMessage && successText) {
      successText.textContent = message;
      successMessage.hidden = false;
    }
  }

  /**
   * Hide success message
   */
  #hideSuccess() {
    const { successMessage } = this.refs;
    if (successMessage) {
      successMessage.hidden = true;
    }
  }

  /**
   * Update recipe dropdowns with loaded recipes and created drafts
   */
  #updateRecipeDropdowns() {
    const { singleRecipeSelect, singleRecipeContainer } = this.refs;

    // Show dropdown container if we have recipes or drafts to show (but not in multiple mode)
    const hasOptions = this.#loadedRecipes.length > 0 || this.#createdDrafts.length > 0;
    if (singleRecipeContainer && !this.#singleRecipe) {
      singleRecipeContainer.hidden = !hasOptions || this.#mode === 'multiple';
    }

    // Update single recipe dropdown
    if (singleRecipeSelect) {
      const currentValue = singleRecipeSelect.value;
      singleRecipeSelect.innerHTML = `<option value="">${this.dataset.selectPlaceholder || 'Vyberte směs'}</option>`;

      // Add created drafts first (if any)
      if (this.#createdDrafts.length > 0) {
        for (const draft of this.#createdDrafts) {
          const option = document.createElement('option');
          option.value = `draft_${draft.localId}`;
          option.textContent = `✨ ${draft.displayName}`;
          singleRecipeSelect.appendChild(option);
        }
      }

      // Add loaded recipes
      for (const recipe of this.#loadedRecipes) {
        const option = document.createElement('option');
        option.value = recipe.id.toString();
        option.textContent = recipe.name;
        singleRecipeSelect.appendChild(option);
      }

      // Restore selection if still valid (check both drafts and loaded recipes)
      if (currentValue) {
        const isDraft = currentValue.startsWith('draft_');
        const isValidDraft = isDraft && this.#createdDrafts.some((d) => `draft_${d.localId}` === currentValue);
        const isValidLoaded = !isDraft && this.#loadedRecipes.some((r) => r.id.toString() === currentValue);

        if (isValidDraft || isValidLoaded) {
          singleRecipeSelect.value = currentValue;
        }
      }
    }

    // Update slot dropdowns in multiple mode
    this.#updateSlotDropdowns();
  }

  /**
   * Update slot dropdowns in multiple recipes mode
   */
  #updateSlotDropdowns() {
    const { slotsContainer } = this.refs;
    if (!slotsContainer) return;

    const selects = slotsContainer.querySelectorAll('select[data-slot-select]');
    selects.forEach((select) => {
      const selectEl = /** @type {HTMLSelectElement} */ (select);
      const currentValue = selectEl.value;

      selectEl.innerHTML = `<option value="">${this.dataset.selectPlaceholder || 'Vyberte recept'}</option>`;

      // Add created drafts first (if any)
      if (this.#createdDrafts.length > 0) {
        for (const draft of this.#createdDrafts) {
          const option = document.createElement('option');
          option.value = `draft_${draft.localId}`;
          option.textContent = `✨ ${draft.displayName}`;
          selectEl.appendChild(option);
        }
      }

      // Add loaded recipes
      for (const recipe of this.#loadedRecipes) {
        const option = document.createElement('option');
        option.value = recipe.id.toString();
        option.textContent = recipe.name;
        selectEl.appendChild(option);
      }

      // Restore selection
      if (currentValue) {
        selectEl.value = currentValue;
      }
    });
  }

  /**
   * Handle multiple recipes checkbox change
   * @param {Event} event
   */
  handleMultipleModeChange(event) {
    const checkbox = /** @type {HTMLInputElement} */ (event.target);
    const newMode = checkbox.checked ? 'multiple' : 'single';

    // When switching from single to multiple, pre-fill first slot with single recipe
    if (newMode === 'multiple' && this.#mode === 'single' && this.#singleRecipe) {
      // Ensure we have enough slots
      while (this.#recipes.length < this.#quantity) {
        this.#recipes.push(/** @type {RecipeDraft} */ ({ localId: '', displayName: '', isEditable: false }));
      }
      // Copy single recipe to first slot
      this.#recipes[0] = { ...this.#singleRecipe };
    }

    // When switching from multiple to single, use first non-empty slot recipe
    if (newMode === 'single' && this.#mode === 'multiple') {
      const firstFilledSlot = this.#recipes.find((r) => r.localId !== '');
      if (firstFilledSlot) {
        this.#singleRecipe = { ...firstFilledSlot };
      }
    }

    this.#mode = newMode;
    this.#updateModeUI();
    this.#dispatchRecipesChanged();
  }

  /**
   * Update UI based on mode (single/multiple)
   */
  #updateModeUI() {
    const { singleRecipeContainer, singleSelectedRecipe, multipleRecipesContainer } = this.refs;

    // Hide success/error messages when mode changes
    this.#hideSuccess();
    this.#hideError();

    if (singleRecipeContainer) {
      singleRecipeContainer.hidden = this.#mode === 'multiple';
    }

    // Hide selected recipe display in multiple mode
    if (singleSelectedRecipe) {
      singleSelectedRecipe.hidden = this.#mode === 'multiple' || !this.#singleRecipe;
    }

    if (multipleRecipesContainer) {
      multipleRecipesContainer.hidden = this.#mode === 'single';
    }

    if (this.#mode === 'multiple') {
      this.#renderSlots();
    } else {
      // Update single recipe UI when switching back
      this.#updateSingleRecipeUI();
    }
  }

  /**
   * Update visibility of multiple recipes toggle (only show when qty > 1)
   */
  #updateMultipleToggleVisibility() {
    const { multipleRecipesToggle, multipleRecipesCheckbox } = this.refs;

    if (multipleRecipesToggle) {
      multipleRecipesToggle.hidden = this.#quantity <= 1;
    }

    // If quantity becomes 1, switch back to single mode
    if (this.#quantity <= 1 && this.#mode === 'multiple') {
      this.#mode = 'single';
      if (multipleRecipesCheckbox) {
        multipleRecipesCheckbox.checked = false;
      }
      this.#updateModeUI();
    }
  }

  /**
   * Handle quantity change from quantity selector
   * @param {CustomEvent<{quantity: number}>} event
   */
  #onQuantityChanged = (event) => {
    const newQuantity = event.detail.quantity;
    if (newQuantity !== this.#quantity) {
      this.#quantity = newQuantity;
      this.#updateMultipleToggleVisibility();

      // Adjust slots if in multiple mode
      if (this.#mode === 'multiple') {
        this.#adjustSlots();
      }
    }
  };

  /**
   * Adjust number of slots to match quantity
   */
  #adjustSlots() {
    while (this.#recipes.length < this.#quantity) {
      this.#recipes.push(/** @type {RecipeDraft} */ ({ localId: '', displayName: '', isEditable: false }));
    }

    while (this.#recipes.length > this.#quantity) {
      this.#recipes.pop();
    }

    this.#renderSlots();
    // Sloty se změnily, takže product-form musí přepočítat validitu.
    // Bez toho zůstane #recipesValid zastarale true a prázdný slot projde
    // až do add-to-cart.
    this.#dispatchRecipesChanged();
  }

  /**
   * Render recipe slots for multiple mode
   */
  #renderSlots() {
    const { slotsContainer } = this.refs;
    if (!slotsContainer) return;

    // Ensure we have enough slots
    while (this.#recipes.length < this.#quantity) {
      this.#recipes.push(/** @type {RecipeDraft} */ ({ localId: '', displayName: '', isEditable: false }));
    }

    slotsContainer.innerHTML = '';

    for (let i = 0; i < this.#quantity; i++) {
      const recipe = this.#recipes[i];
      const slot = this.#createSlotElement(i, recipe);
      slotsContainer.appendChild(slot);
    }
  }

  /**
   * Create a slot element for a recipe
   * @param {number} index
   * @param {RecipeDraft} recipe
   * @returns {HTMLElement}
   */
  #createSlotElement(index, recipe) {
    const slot = document.createElement('div');
    slot.className = 'recipe-slot';
    slot.dataset.slotIndex = index.toString();

    const label = document.createElement('div');
    label.className = 'recipe-slot__label';
    label.textContent = `${this.dataset.slotLabel || 'Kus'} ${index + 1}`;
    slot.appendChild(label);

    const controls = document.createElement('div');
    controls.className = 'recipe-slot__controls';

    if (recipe.localId) {
      // Recipe is selected - show name and edit button if editable
      const selected = document.createElement('div');
      selected.className = 'recipe-slot__selected';

      const name = document.createElement('span');
      name.className = 'recipe-slot__name';
      name.textContent = recipe.displayName;
      selected.appendChild(name);

      if (recipe.isEditable) {
        const editBtn = document.createElement('button');
        editBtn.type = 'button';
        editBtn.className = 'button-unstyled recipe-slot__edit';
        editBtn.textContent = this.dataset.editText || 'Upravit';
        editBtn.addEventListener('click', () => this.#editSlotRecipe(index));
        selected.appendChild(editBtn);
      } else {
        const lock = document.createElement('span');
        lock.className = 'recipe-slot__lock';
        lock.textContent = '🔒';
        lock.title = this.dataset.lockedText || 'Recept nelze upravit';
        selected.appendChild(lock);
      }

      const changeBtn = document.createElement('button');
      changeBtn.type = 'button';
      changeBtn.className = 'button-unstyled recipe-slot__change';
      changeBtn.textContent = this.dataset.changeText || 'Změnit';
      changeBtn.addEventListener('click', () => this.#clearSlotRecipe(index));
      selected.appendChild(changeBtn);

      controls.appendChild(selected);
    } else {
      // No recipe selected - show dropdown and new button
      const select = document.createElement('select');
      select.className = 'recipe-slot__select';
      select.dataset.slotSelect = index.toString();
      select.innerHTML = `<option value="">${this.dataset.selectPlaceholder || 'Vyberte recept'}</option>`;

      // Add created drafts first (if any)
      for (const draft of this.#createdDrafts) {
        const option = document.createElement('option');
        option.value = `draft_${draft.localId}`;
        option.textContent = `✨ ${draft.displayName}`;
        select.appendChild(option);
      }

      // Add loaded recipes
      for (const r of this.#loadedRecipes) {
        const option = document.createElement('option');
        option.value = r.id.toString();
        option.textContent = r.name;
        select.appendChild(option);
      }

      select.addEventListener('change', (e) => this.#onSlotSelectChange(index, e));
      controls.appendChild(select);

      const newBtn = document.createElement('button');
      newBtn.type = 'button';
      newBtn.className = 'button button-secondary recipe-slot__new';
      newBtn.textContent = this.dataset.newText || '+ Nový';
      newBtn.addEventListener('click', () => this.#createNewSlotRecipe(index));
      controls.appendChild(newBtn);
    }

    slot.appendChild(controls);
    return slot;
  }

  /**
   * Handle slot select change
   * @param {number} index
   * @param {Event} event
   */
  #onSlotSelectChange(index, event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const value = select.value;

    if (!value) {
      this.#recipes[index] = /** @type {RecipeDraft} */ ({ localId: '', displayName: '', isEditable: false });
    } else if (value.startsWith('draft_')) {
      // Selected a created draft
      const draftLocalId = value.replace('draft_', '');
      const draft = this.#createdDrafts.find((d) => d.localId === draftLocalId);
      if (draft) {
        // Copy the draft to this slot
        this.#recipes[index] = { ...draft };
      }
    } else {
      // Selected a loaded recipe
      const recipeId = parseInt(value, 10);
      const recipe = this.#loadedRecipes.find((r) => r.id === recipeId);
      if (recipe) {
        this.#recipes[index] = {
          localId: this.#generateLocalId(),
          templateRecipeId: recipeId,
          displayName: recipe.name,
          isEditable: false,
        };
      }
    }

    this.#renderSlots();
    this.#dispatchRecipesChanged();
  }

  /**
   * Create new recipe for a specific slot
   * @param {number} index
   */
  #createNewSlotRecipe(index) {
    this.dataset.activeSlotIndex = index.toString();
    this.dataset.activeMode = this.#mode;
    this.#openRecipeModal();
  }

  /**
   * Edit recipe for a specific slot
   * @param {number} index
   */
  #editSlotRecipe(index) {
    const recipe = this.#recipes[index];
    if (!recipe || !recipe.isEditable) return;

    this.dataset.activeSlotIndex = index.toString();
    this.dataset.activeMode = this.#mode;
    this.#openRecipeModal(recipe);
  }

  /**
   * Clear recipe for a specific slot
   * @param {number} index
   */
  #clearSlotRecipe(index) {
    this.#recipes[index] = /** @type {RecipeDraft} */ ({ localId: '', displayName: '', isEditable: false });
    this.#renderSlots();
    this.#dispatchRecipesChanged();
  }

  /**
   * Handle single recipe select change
   * @param {Event} event
   */
  handleSingleRecipeChange(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const value = select.value;

    if (!value) {
      this.#singleRecipe = null;
    } else if (value.startsWith('draft_')) {
      // Selected a created draft
      const draftLocalId = value.replace('draft_', '');
      const draft = this.#createdDrafts.find((d) => d.localId === draftLocalId);
      if (draft) {
        this.#singleRecipe = { ...draft };
      }
    } else {
      // Selected a loaded recipe
      const recipeId = parseInt(value, 10);
      const recipe = this.#loadedRecipes.find((r) => r.id === recipeId);
      if (recipe) {
        this.#singleRecipe = {
          localId: this.#generateLocalId(),
          templateRecipeId: recipeId,
          displayName: recipe.name,
          isEditable: false,
        };
      }
    }

    // Hide success/error messages when recipe is selected
    if (this.#singleRecipe) {
      this.#hideSuccess();
      this.#hideError();
    }

    this.#updateSingleRecipeUI();
    this.#dispatchRecipesChanged();
  }

  /**
   * Handle create new recipe button click (single mode)
   */
  handleCreateNewRecipe() {
    this.dataset.activeSlotIndex = '';
    this.dataset.activeMode = this.#mode;
    this.#openRecipeModal();
  }

  /**
   * Handle edit recipe button click (single mode)
   */
  handleEditRecipe() {
    if (!this.#singleRecipe || !this.#singleRecipe.isEditable) return;
    this.dataset.activeSlotIndex = '';
    this.dataset.activeMode = this.#mode;
    this.#openRecipeModal(this.#singleRecipe);
  }

  /**
   * Handle change recipe button click (single mode)
   */
  handleChangeRecipe() {
    this.#singleRecipe = null;
    this.#updateSingleRecipeUI();
    this.#dispatchRecipesChanged();
  }

  /**
   * Update single recipe UI
   */
  #updateSingleRecipeUI() {
    const {
      singleRecipeContainer,
      singleSelectedRecipe,
      singleSelectedName,
      singleEditButton,
      primaryAction,
      divider,
      emailSection,
      helperText,
    } = this.refs;

    const hasRecipe = this.#singleRecipe !== null;

    // Hide dropdown container when recipe is selected
    if (singleRecipeContainer) singleRecipeContainer.hidden = hasRecipe;

    // Show selected recipe display (but not in multiple mode)
    if (singleSelectedRecipe) singleSelectedRecipe.hidden = !hasRecipe || this.#mode === 'multiple';

    // Hide primary action, divider, email section when recipe is selected
    // (fallback for browsers without :has() support)
    if (primaryAction) primaryAction.hidden = hasRecipe;
    if (divider) divider.hidden = hasRecipe;
    if (emailSection) emailSection.hidden = hasRecipe;

    // Hide helper text when recipe is selected
    if (helperText) helperText.hidden = hasRecipe;

    // Update selected name
    if (hasRecipe && singleSelectedName) {
      singleSelectedName.textContent = this.#singleRecipe?.displayName || '';
    }

    // Show edit button only for editable drafts
    if (singleEditButton) {
      singleEditButton.hidden = !hasRecipe || !this.#singleRecipe?.isEditable;
    }
  }

  /**
   * Open the recipe modal
   * @param {RecipeDraft} [editRecipe] - Recipe to edit
   */
  #openRecipeModal(editRecipe) {
    const modal = /** @type {any} */ (document.querySelector('recipe-modal-component'));
    if (!modal) return;

    if (editRecipe) {
      modal.setEditMode(editRecipe);
    } else {
      modal.setCreateMode();
    }

    modal.showDialog();
  }

  /**
   * Handle recipe created/updated from modal
   * @param {CustomEvent<RecipeDraft>} event
   */
  #onRecipeCreated = (event) => {
    const draft = event.detail;
    const slotIndexStr = this.dataset.activeSlotIndex;
    const storedMode = this.dataset.activeMode || this.#mode;

    // Add to created drafts if not already there (for dropdown availability)
    if (!this.#createdDrafts.some((d) => d.localId === draft.localId)) {
      this.#createdDrafts.push(draft);
    } else {
      // Update existing draft
      const idx = this.#createdDrafts.findIndex((d) => d.localId === draft.localId);
      if (idx !== -1) {
        this.#createdDrafts[idx] = draft;
      }
    }

    // Use stored mode (from when modal was opened) to determine where to put recipe
    if (storedMode === 'multiple' && slotIndexStr !== undefined && slotIndexStr !== '') {
      // Multiple mode - update specific slot
      const slotIndex = parseInt(slotIndexStr, 10);
      this.#recipes[slotIndex] = draft;
      this.#renderSlots();
    } else if (storedMode === 'single') {
      // Single mode
      this.#singleRecipe = draft;
      this.#updateSingleRecipeUI();
    } else if (storedMode === 'multiple') {
      // Multiple mode but no slot index (shouldn't happen, but handle gracefully)
      // Put in first empty slot
      const emptySlotIndex = this.#recipes.findIndex((r) => r.localId === '');
      if (emptySlotIndex !== -1) {
        this.#recipes[emptySlotIndex] = draft;
      } else {
        this.#recipes[0] = draft;
      }
      this.#renderSlots();
    }

    // Update dropdowns to include the new draft
    this.#updateRecipeDropdowns();

    delete this.dataset.activeSlotIndex;
    delete this.dataset.activeMode;
    this.#dispatchRecipesChanged();
  };

  /**
   * Dispatch event when recipes selection changes
   */
  #dispatchRecipesChanged() {
    this.dispatchEvent(
      new CustomEvent('recipes:changed', {
        bubbles: true,
        detail: {
          mode: this.#mode,
          singleRecipe: this.#singleRecipe,
          recipes: this.#recipes,
          quantity: this.#quantity,
          customerEmail: this.#customerEmail,
        },
      })
    );
  }

  /**
   * Get current recipes for cart submission
   * @returns {{mode: 'single' | 'multiple', singleRecipe: RecipeDraft | null, recipes: RecipeDraft[], quantity: number, customerEmail: string}}
   */
  getRecipes() {
    // Fallback to email input value only if it looks like a valid email
    const inputEmail = this.refs.emailInput?.value?.trim() || '';
    const email = this.#customerEmail || (inputEmail.includes('@') ? inputEmail : '');
    return {
      mode: this.#mode,
      singleRecipe: this.#singleRecipe,
      recipes: this.#recipes,
      quantity: this.#quantity,
      customerEmail: email,
    };
  }

  /**
   * Check if all required recipes are selected
   * @returns {boolean}
   */
  isValid() {
    if (this.#mode === 'single') {
      return this.#singleRecipe !== null;
    } else {
      return this.#recipes.slice(0, this.#quantity).every((r) => r.localId !== '');
    }
  }

  /**
   * Set quantity externally (from product form)
   * @param {number} quantity
   */
  setQuantity(quantity) {
    if (quantity !== this.#quantity) {
      this.#quantity = quantity;
      this.dataset.quantity = quantity.toString();
      this.#updateMultipleToggleVisibility();

      if (this.#mode === 'multiple') {
        this.#adjustSlots();
      }
    }
  }
}

if (!customElements.get('recipe-selector-component')) {
  customElements.define('recipe-selector-component', RecipeSelectorComponent);
}

export { RecipeSelectorComponent };
